import { Flex, HStack, TabList, TabPanel, Tabs } from "@chakra-ui/react";
import { IconButton, Layout, PageMotion, PoweredBy, Tab, TabPanels } from "components";
import { useAuthToken } from "hooks";
import { PageProps } from "interfaces";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { BuyPanel, SellPanel } from "ui";
import { toCoinLocale } from "utils";
import ls from "utils/secureStorage";

interface BuyAndSellProps extends PageProps {}

export default function BuyAndSell(props: BuyAndSellProps) {
  const location = useLocation();
  const navigate = useNavigate();

  const searchParams = new URLSearchParams(location?.search as string);
  const queries = useMemo(() => new URLSearchParams(location?.search), [location]);
  const isLocked = useMemo(() => queries.get("locked"), [queries]);
  const method = useMemo(() => queries.get("method") ?? "", [queries]);

  const tabMap = {
    buy: 0,
    sell: isLocked === "true" ? undefined : 1,
  };

  const [tabIndex, changeTab] = useState((tabMap as any)[searchParams.get("tab") as any] ?? 0);

  const handleTabChange = (index: number) => {
    changeTab(index);
    searchParams.set("tab", Object.keys(tabMap)[index]);
    navigate(`?${searchParams.toString()}`);
  };

  const coin = queries.get("coin");

  const allParameters = useMemo(() => {
    return queries.has("coin") && queries.has("fiat") && queries.has("address");
  }, [queries]);

  const { authToken } = useAuthToken();

  const currentTab = useMemo(() => queries.get("tab"), [queries]);
  const qrCode = useMemo(() => queries.get("qr"), [queries]);

  useEffect(() => {
    ls.set("tabType", currentTab);
    ls.set("method", method);
    ls.set("qrcode", qrCode);
  }, [currentTab, method, qrCode]);

  return (
    <PageMotion key="BuyAndSell" minH={{ base: "96vh", md: "98vh" }}>
      <Layout>
        <Tabs variant="ghost" isLazy index={tabIndex} onChange={handleTabChange}>
          <HStack w="100%">
            <Flex flex="1" />

            <TabList flex={{ base: "6", "2sm": isLocked === "true" ? "1" : "4" }} borderRadius="8px">
              {isLocked === "true" ? (
                <Tab w="100%" fontSize="md">
                  {currentTab === "buy" || !currentTab
                    ? `Buy ${(isLocked === "true" && coin) || allParameters ? toCoinLocale(coin?.toLowerCase()!) : "Crypto"}`
                    : `Sell ${(isLocked === "true" && coin) || allParameters ? toCoinLocale(coin?.toLowerCase()!) : "Crypto"}`}
                </Tab>
              ) : (
                <>
                  <Tab w="100%" fontSize="md">
                    Buy Crypto
                  </Tab>

                  <Tab fontSize="md">Sell Crypto</Tab>
                </>
              )}
            </TabList>

            <Flex flex="1" />
            <IconButton aria-label="more button" icon="moreVert" />
          </HStack>

          {(!isLocked || isLocked !== "true") && (
            <TabPanels index={tabIndex} onChangeIndex={handleTabChange}>
              <TabPanel padding={{ base: "1rem 8px", "2sm": "1rem" }}>
                <BuyPanel authToken={authToken!} {...props} />
              </TabPanel>
              <TabPanel hidden padding={{ base: "1rem 8px", "2sm": "1rem" }}>
                <SellPanel authToken={authToken!} {...props} />
              </TabPanel>
            </TabPanels>
          )}

          {isLocked && isLocked === "true" && (
            <TabPanels index={tabIndex} onChangeIndex={handleTabChange}>
              <TabPanel padding={{ base: "1rem 8px", "2sm": "1rem" }}>
                {currentTab === "buy" || !currentTab ? (
                  <BuyPanel authToken={authToken!} {...props} />
                ) : (
                  <SellPanel authToken={authToken!} {...props} />
                )}
              </TabPanel>
            </TabPanels>
          )}
        </Tabs>
      </Layout>

      <PoweredBy bottom={{ base: "3%", md: "1%" }} />
    </PageMotion>
  );
}
